import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { uploadPromptDocumentDirect } from '../Prompts/promptDocumentUpload';
import type { PromptVideoUploadPhase } from '../Prompts/promptVideoUploadTracker';
import { reindexNumericRecord } from './useCreatePromptVideoUploads';

export type CreatePromptDocumentUploadState = {
  phase: PromptVideoUploadPhase;
  percent: number;
  error?: string;
};

function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function reindexRefRecord<T>(record: Record<number, T>, removedIndex: number) {
  const re: Record<number, T> = {};
  Object.entries(record).forEach(([k, value]) => {
    const oldIdx = parseInt(k, 10);
    if (oldIdx === removedIndex) return;
    const newIdx = oldIdx > removedIndex ? oldIdx - 1 : oldIdx;
    re[newIdx] = value;
  });
  return re;
}

export function useCreatePromptDocumentUploads(documentFiles: Record<number, File>) {
  const [documentUrls, setDocumentUrls] = useState<Record<number, string>>({});
  const [documentUploads, setDocumentUploads] = useState<
    Record<number, CreatePromptDocumentUploadState>
  >({});
  const uploadAbortRef = useRef<Record<number, boolean>>({});
  const uploadAbortFnRef = useRef<Record<number, () => void>>({});
  const uploadedFileKeyRef = useRef<Record<number, string>>({});
  const inFlightRef = useRef<Set<number>>(new Set());
  const uploadErrorIndicesRef = useRef<Set<number>>(new Set());
  const documentUrlsRef = useRef(documentUrls);
  documentUrlsRef.current = documentUrls;

  const clearDocumentUploadState = useCallback((index: number) => {
    uploadAbortFnRef.current[index]?.();
    delete uploadAbortFnRef.current[index];
    uploadAbortRef.current[index] = true;
    inFlightRef.current.delete(index);
    uploadErrorIndicesRef.current.delete(index);
    delete uploadedFileKeyRef.current[index];
    setDocumentUrls((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setDocumentUploads((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const reindexDocumentUploadState = useCallback((removedIndex: number) => {
    setDocumentUrls((prev) => reindexNumericRecord(prev, removedIndex));
    setDocumentUploads((prev) => reindexNumericRecord(prev, removedIndex));
    uploadAbortRef.current = reindexRefRecord(uploadAbortRef.current, removedIndex);
    uploadedFileKeyRef.current = reindexRefRecord(uploadedFileKeyRef.current, removedIndex);
    inFlightRef.current = new Set(
      Array.from(inFlightRef.current)
        .filter((idx) => idx !== removedIndex)
        .map((idx) => (idx > removedIndex ? idx - 1 : idx))
    );
    uploadErrorIndicesRef.current = new Set(
      Array.from(uploadErrorIndicesRef.current)
        .filter((idx) => idx !== removedIndex)
        .map((idx) => (idx > removedIndex ? idx - 1 : idx))
    );
  }, []);

  const uploadDocumentAtIndex = useCallback((index: number, file: File) => {
    if (inFlightRef.current.has(index)) {
      return;
    }

    const fk = fileKey(file);
    const abortFns: Array<() => void> = [];
    inFlightRef.current.add(index);
    uploadAbortRef.current[index] = false;
    uploadAbortFnRef.current[index] = () => {
      abortFns.forEach((abort) => abort());
    };

    setDocumentUrls((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setDocumentUploads((prev) => ({
      ...prev,
      [index]: { phase: 'presigning', percent: 2 },
    }));

    void uploadPromptDocumentDirect(file, {
      registerAbort: (abort) => {
        abortFns.push(abort);
      },
      onProgress: (progress) => {
        if (uploadAbortRef.current[index]) return;
        setDocumentUploads((prev) => ({
          ...prev,
          [index]: { phase: progress.phase, percent: progress.percent },
        }));
      },
    })
      .then((publicUrl) => {
        if (uploadAbortRef.current[index]) return;
        uploadedFileKeyRef.current[index] = fk;
        uploadErrorIndicesRef.current.delete(index);
        setDocumentUrls((prev) => ({ ...prev, [index]: publicUrl }));
        setDocumentUploads((prev) => ({
          ...prev,
          [index]: { phase: 'done', percent: 100 },
        }));
      })
      .catch((err: unknown) => {
        if (uploadAbortRef.current[index]) return;
        const message = err instanceof Error ? err.message : 'Document upload failed.';
        setDocumentUploads((prev) => ({
          ...prev,
          [index]: { phase: 'error', percent: 0, error: message },
        }));
        uploadErrorIndicesRef.current.add(index);
        toast.error(message);
      })
      .finally(() => {
        inFlightRef.current.delete(index);
        delete uploadAbortFnRef.current[index];
      });
  }, []);

  const cancelDocumentUpload = useCallback((index: number) => {
    uploadAbortRef.current[index] = true;
    uploadAbortFnRef.current[index]?.();
    delete uploadAbortFnRef.current[index];
    inFlightRef.current.delete(index);
    setDocumentUploads((prev) => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const retryDocumentUpload = useCallback(
    (index: number) => {
      const file = documentFiles[index];
      if (!file) {
        toast.error('No document found to retry.');
        return;
      }
      delete uploadedFileKeyRef.current[index];
      uploadErrorIndicesRef.current.delete(index);
      uploadDocumentAtIndex(index, file);
    },
    [documentFiles, uploadDocumentAtIndex]
  );

  useEffect(() => {
    Object.entries(documentFiles).forEach(([key, file]) => {
      const index = parseInt(key, 10);
      const fk = fileKey(file);
      if (uploadedFileKeyRef.current[index] === fk && documentUrlsRef.current[index]) {
        return;
      }
      if (uploadErrorIndicesRef.current.has(index)) {
        return;
      }
      if (inFlightRef.current.has(index)) {
        return;
      }

      uploadDocumentAtIndex(index, file);
    });
  }, [documentFiles, clearDocumentUploadState, uploadDocumentAtIndex]);

  const hasPendingDocumentUploads = useCallback(() => {
    return Object.keys(documentFiles).some((key) => {
      const index = parseInt(key, 10);
      if (documentUrls[index]) return false;
      const upload = documentUploads[index];
      return !upload || upload.phase !== 'done';
    });
  }, [documentFiles, documentUrls, documentUploads]);

  const hasDocumentUploadErrors = useCallback(() => {
    return Object.values(documentUploads).some((u) => u.phase === 'error');
  }, [documentUploads]);

  const seedDocumentUrls = useCallback((urls: Record<number, string>) => {
    setDocumentUrls((prev) => ({ ...prev, ...urls }));
  }, []);

  const replaceDocumentUrls = useCallback((urls: Record<number, string>) => {
    uploadedFileKeyRef.current = {};
    inFlightRef.current.clear();
    uploadErrorIndicesRef.current.clear();
    uploadAbortRef.current = {};
    setDocumentUploads({});
    setDocumentUrls(urls);
  }, []);

  return {
    documentUrls,
    documentUploads,
    clearDocumentUploadState,
    reindexDocumentUploadState,
    retryDocumentUpload,
    cancelDocumentUpload,
    hasPendingDocumentUploads,
    hasDocumentUploadErrors,
    seedDocumentUrls,
    replaceDocumentUrls,
  };
}
